import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import {
  Alert,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  StyledCrmDialog,
  CrmDialogTitle,
  CrmDialogCloseButton,
  CrmDialogContent,
  CrmDialogActions,
  CrmField,
  CrmPrimaryButton,
  CrmSecondaryButton,
  CrmPanel,
} from './AdminCrm.styled';

function platformBusinessId(business) {
  return business?.BusinessId || business?.id || '';
}

export function searchSeedFromProspect(prospect) {
  const name = String(
    prospect?.businessName || prospect?.ownerContact || '',
  ).trim();
  return name.slice(0, 5);
}

function AdminCrmLinkBusinessModal({
  open,
  prospect,
  businesses = [],
  onClose,
  onLink,
  onUnlink,
  saving = false,
}) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch(searchSeedFromProspect(prospect));
    setSelectedId(prospect?.businessId || '');
    setError(null);
  }, [open, prospect]);

  const sortedBusinesses = useMemo(
    () =>
      [...businesses].sort((a, b) =>
        String(a.BusinessName || '').localeCompare(
          String(b.BusinessName || ''),
          undefined,
          { sensitivity: 'base' },
        ),
      ),
    [businesses],
  );

  const filteredBusinesses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedBusinesses;
    return sortedBusinesses.filter((business) => {
      const haystack = [
        business.BusinessName,
        platformBusinessId(business),
        business.BusinessEmail,
        business.BusinessPhoneNumber,
        business.BusinessCity,
        business.BusinessAddress?.City,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedBusinesses, search]);

  const handleLink = async () => {
    if (!selectedId) {
      setError('Select a platform business to link.');
      return;
    }
    setError(null);
    try {
      await onLink(selectedId);
    } catch (e) {
      setError(e.message || 'Failed to link business');
    }
  };

  const handleUnlink = async () => {
    setError(null);
    try {
      await onUnlink();
    } catch (e) {
      setError(e.message || 'Failed to unlink business');
    }
  };

  const prospectLabel =
    prospect?.businessName || prospect?.ownerContact || 'this prospect';

  return (
    <StyledCrmDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="admin-crm-link-business-title"
    >
      <CrmDialogTitle id="admin-crm-link-business-title">
        <span className="crm-dialog-icon-wrap" aria-hidden>
          <LinkIcon sx={{ color: '#0d4efe' }} />
        </span>
        Link platform business
        <CrmDialogCloseButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </CrmDialogCloseButton>
      </CrmDialogTitle>

      <CrmDialogContent>
        <Typography variant="body2" sx={{ color: '#5a6478' }}>
          Choose the platform business that signed up for{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            {prospectLabel}
          </Box>
          .
        </Typography>

        {error && (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        <CrmField
          autoFocus
          fullWidth
          size="small"
          label="Search businesses"
          placeholder="Name, email, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <CrmPanel sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {filteredBusinesses.length === 0 ? (
              <Typography sx={{ color: '#5a6478', p: 2 }}>
                No businesses match “{search.trim() || 'your search'}”.
              </Typography>
            ) : (
              <List disablePadding dense>
                {filteredBusinesses.map((business) => {
                  const id = platformBusinessId(business);
                  const selected = selectedId === id;
                  return (
                    <ListItemButton
                      key={id}
                      selected={selected}
                      disabled={saving}
                      onClick={() => setSelectedId(id)}
                      sx={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(13, 78, 254, 0.08)',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(13, 78, 254, 0.12)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={business.BusinessName || id}
                        secondary={
                          [
                            id,
                            business.BusinessEmail,
                            business.BusinessPhoneNumber,
                          ]
                            .filter(Boolean)
                            .join(' · ') || null
                        }
                        primaryTypographyProps={{ fontWeight: selected ? 700 : 600 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </CrmPanel>
      </CrmDialogContent>

      <CrmDialogActions>
        {Boolean(prospect?.businessId) && (
          <CrmSecondaryButton
            color="error"
            variant="outlined"
            disabled={saving}
            onClick={handleUnlink}
            sx={{
              mr: 'auto',
              borderColor: 'rgba(211,47,47,0.4)',
              color: '#d32f2f',
            }}
          >
            Unlink
          </CrmSecondaryButton>
        )}
        <CrmSecondaryButton variant="outlined" onClick={onClose} disabled={saving}>
          Skip for now
        </CrmSecondaryButton>
        <CrmPrimaryButton onClick={handleLink} disabled={saving || !selectedId}>
          {saving ? 'Linking…' : 'Link business'}
        </CrmPrimaryButton>
      </CrmDialogActions>
    </StyledCrmDialog>
  );
}

export default AdminCrmLinkBusinessModal;
